import { AlignCenterHorizontal, AlignCenterVertical, AlignEndHorizontal, AlignEndVertical, AlignHorizontalDistributeCenter, AlignStartHorizontal, AlignStartVertical, AlignVerticalDistributeCenter, type LucideIcon } from "lucide-preact";
import { useEffect, useRef } from "preact/hooks";
import type { ArrangeOperation } from "../../format/arrange.ts";
import { arrange, clearArrange, finishArrange } from "../arrange.ts";
import { arrangeSelection, canEdit } from "../state.ts";

const ALIGN: [ArrangeOperation, string, string, LucideIcon][] = [
  ["left", "Align left", "Line up the left edges of the selected GO positions.", AlignStartVertical],
  ["horizontal-centres", "Align horizontal centres", "Line up the horizontal centres of the selected GO positions.", AlignCenterVertical],
  ["right", "Align right", "Line up the right edges of the selected GO positions.", AlignEndVertical],
  ["top", "Align top", "Line up the top edges of the selected GO positions.", AlignStartHorizontal],
  ["vertical-centres", "Align vertical centres", "Line up the vertical centres of the selected GO positions.", AlignCenterHorizontal],
  ["bottom", "Align bottom", "Line up the bottom edges of the selected GO positions.", AlignEndHorizontal],
];
const DISTRIBUTE: [ArrangeOperation, string, string, LucideIcon][] = [
  ["distribute-horizontal", "Distribute horizontal centres", "Space the selected GO positions evenly from left to right, keeping the outermost positions fixed.", AlignHorizontalDistributeCenter],
  ["distribute-vertical", "Distribute vertical centres", "Space the selected GO positions evenly from top to bottom, keeping the outermost positions fixed.", AlignVerticalDistributeCenter],
];

export function Arrange() {
  const root = useRef<HTMLElement>(null);
  const selection = arrangeSelection.value;
  useEffect(() => {
    const element = root.current!;
    const update = () => document.documentElement.style.setProperty("--arrange-height", `${element.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(update);
    observer.observe(element);
    update();
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--arrange-height");
    };
  }, []);
  if (!selection) return null;
  const count = selection.ids.length;
  return (
    <section class="arrange-controls" aria-label="Arrange cues" ref={root}>
      <div class="arrange-head">
        <strong role="status">{count} {count === 1 ? "moment" : "moments"} selected</strong>
        <button type="button" class="primary" data-tooltip="Done arranging" data-tooltip-description="Leave arrangement mode and keep the current GO positions." data-tooltip-keys="Esc" onClick={finishArrange}>Done</button>
      </div>
      <div class="arrange-body">
        <div class="arrange-align" role="group" aria-label="Alignment">
          {ALIGN.map(([op, label, description, Icon]) => (
            <button type="button" class="arrange-command" aria-label={label} data-tooltip={label} data-tooltip-description={`${description} Select at least two moments.${!canEdit.value ? " Editing is currently unavailable." : ""}`} disabled={count < 2 || !canEdit.value} onClick={() => arrange(op, label)}>
              <Icon aria-hidden="true" />
            </button>
          ))}
        </div>
        <div class="arrange-distribute" role="group" aria-label="Distribution">
          {DISTRIBUTE.map(([op, label, description, Icon]) => (
            <button type="button" class="arrange-command" aria-label={label} data-tooltip={label} data-tooltip-description={`${description} Select at least three moments.${!canEdit.value ? " Editing is currently unavailable." : ""}`} disabled={count < 3 || !canEdit.value} onClick={() => arrange(op, label)}>
              <Icon aria-hidden="true" />
            </button>
          ))}
        </div>
        <p class="hint">Moves GO positions. Cue order may change.</p>
        <button type="button" data-tooltip="Clear selection" data-tooltip-description="Deselect all moments without changing their GO positions." disabled={!count} onClick={clearArrange}>Clear selection</button>
      </div>
    </section>
  );
}
